if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.productInfoEvent();
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
        
        // NEW: Update discontinued color bar for current selection
        // We need to get the current variant from the selected option values
        const currentVariant = this.getVariantFromSelectedOptions(selectedOptionValues);
        if (currentVariant) {
          this.updateDiscontinuedColorBar(currentVariant);
        }
      }

      // Add this helper method to get variant from selected options
      getVariantFromSelectedOptions(selectedOptionValues) {
        // This depends on your variant data structure
        // You might need to adjust this based on how you store variants
        const variantInput = this.querySelector('input[name="id"]');
        if (!variantInput || !variantInput.value) return null;
        
        // Get variant data from the variant selects
        const variantSelects = this.variantSelectors;
        if (variantSelects) {
          const variantData = variantSelects.querySelector('[data-selected-variant]');
          if (variantData) {
            try {
              return JSON.parse(variantData.textContent);
            } catch (e) {
              console.error('Error parsing variant data:', e);
            }
          }
        }
        
        return null;
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateDiscontinuedColorBar(variant, html);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            if(id === "price"){
              const sources = html.querySelectorAll(`#${id}-${this.sectionId}`);
              const destinations = this.querySelectorAll(`#${id}-${this.dataset.section}`);
              if (sources && destinations) {
                for (let index = 0; index < sources.length; index++) {
                  let source = sources[index]
                  let destination = destinations[index];
                  if(destination && source){
                      destination.innerHTML = source.innerHTML;
                      destination.classList.toggle('hidden', shouldHide(source));    
                  }
                }
              }
            }else {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            }
            
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('pricetwo');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('LowStock', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'LowStock', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();
        
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      productInfoHideShow(sectionID,productID){
          window.addEventListener('click',function(event){
            console.log("testt");
            var proinfo = document.getElementById(`ProductInfo-${sectionID }-${ productID }`);
              if(proinfo === event.target || proinfo.contains(event.target)) return;
              const fieldsets = proinfo.querySelectorAll(".product-form__input--pill");
              fieldsets.forEach((fieldset) => {
                fieldset.style.display = 'none';
              });
              const all_li_open = proinfo.closest('.grid__item');
              if(all_li_open.classList.contains("open")){
                all_li_open.classList.remove("open")
              }
              proinfo.querySelector(".product-form").style.display ="none";
          });
      }

      updateDiscontinuedColorBar(variant, html) {
        const discontinuedBar = document.getElementById('discontinued-color-bar');
        if (!discontinuedBar) return;

        const color = this.extractColorFromVariant(variant);
        if (!color) return;

        if (window.DiscontinuedColorBar) {
          window.DiscontinuedColorBar.handleColorChange(color);
          return;
        }
        this.updateDiscontinuedBarManually(color);
      }

      extractColorFromVariant(variant) {
        if (!variant || !variant.options) return null;

        const colorOptionIndex = this.getColorOptionIndex();
        if (colorOptionIndex >= 0 && colorOptionIndex < variant.options.length) {
          return variant.options[colorOptionIndex];
        }

        for (let option of variant.options) {
          if (this.isLikelyColor(option)) {
            return option;
          }
        }

        return variant.options[0]; 
      }

      getColorOptionIndex() {
        const colorOptionElement = document.getElementById('discontinued-color-bar');
        if (colorOptionElement) {
          const index = colorOptionElement.getAttribute('data-color-option-index');
          if (index !== null && !isNaN(parseInt(index))) {
            return parseInt(index);
          }
        }

        const variantSelects = this.variantSelectors;
        if (variantSelects) {
          const optionNames = variantSelects.querySelectorAll('.product-form__input legend');
          for (let i = 0; i < optionNames.length; i++) {
            const text = optionNames[i].textContent.toLowerCase();
            if (text.includes('color') || text.includes('colour')) {
              return i;
            }
          }
        }

        return -1; // Not found
      }

      isLikelyColor(optionValue) {
        const colorKeywords = [
          'red', 'blue', 'green', 'black', 'white', 'brown', 'tan', 'leather',
          'camel', 'navy', 'grey', 'gray', 'pink', 'purple', 'yellow', 'orange',
          'gold', 'silver', 'bronze', 'beige', 'khaki', 'maroon', 'burgundy',
          'teal', 'turquoise', 'lavender', 'mauve', 'cream', 'ivory', 'charcoal'
        ];
        
        const lowerOption = optionValue.toLowerCase();
        return colorKeywords.some(keyword => lowerOption.includes(keyword));
      }

      updateDiscontinuedBarManually(color) {
        const bar = document.getElementById('discontinued-color-bar');
        if (!bar) return;

        const textEl = bar.querySelector('.discontinued-color-bar__text');
        if (!textEl) return;

        const rawData = bar.getAttribute('data-discontinued-colors');
        if (!rawData) return;

        try {
          const discontinuedColors = JSON.parse(rawData);
          const normalizedColor = color.toLowerCase().trim();
          
          const discontinuedEntry = discontinuedColors.find(item => 
            item.color.toLowerCase().trim() === normalizedColor
          );

          if (discontinuedEntry && discontinuedEntry.date) {
            const date = new Date(discontinuedEntry.date);
            const month = date.toLocaleDateString(undefined, { month: 'short' });
            const day = date.getDate();
            const ordinal = this.getOrdinal(day);
            
            // Show the bar
            const message = window.product_discontinue_message?.text.replace('#Date', `${month} ${day}${ordinal}`) 
              || `Order soon. This color is leaving ${month} ${day}${ordinal}.`;
            
            textEl.textContent = message;
            bar.hidden = false;
          } else {
            bar.hidden = true;
            textEl.textContent = '';
          }
        } catch (e) {
          console.error('Error updating discontinued color bar:', e);
        }
      }

      getOrdinal(day) {
        if (day > 3 && day < 21) return 'th';
        const last = day % 10;
        if (last === 1) return 'st';
        if (last === 2) return 'nd';
        if (last === 3) return 'rd';
        return 'th';
      }

      productInfoEvent(){
          const quick_add_more = document.querySelectorAll(".quick-add-more");
        console.log("quick_add_more=>",quick_add_more);
          const fieldsetall = document.querySelectorAll('.card-information product-info fieldset.product-form__input--pill');
          fieldsetall.forEach((fieldset) => {
            fieldset.style.display = 'none';
          });
          quick_add_more.forEach((item) => {
            item.addEventListener("click",function(e){
              const parent = e.currentTarget.parentNode;
              e.currentTarget.closest('.grid__item').classList.add("open");
              const fieldsets = parent.querySelectorAll(".product-form__input--pill");
              fieldsets.forEach((fieldset) => {
                fieldset.style.display = 'block';
              });
              parent.querySelector(".product-form").style.display ="block";
            });
          });
        }
      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('swatch-details')) {
  customElements.define(
    'swatch-details',
    class swatchDetails extends HTMLElement {
      variantSelectors ="";
      hiddenVariantInput ="";
      dataVaraint = [];
      constructor() {
        super();
        var thiss = this;
        this.productInfoEvent(thiss);
          const option_id = thiss;
           this.variantSelectors = option_id.querySelectorAll('.product-form__input input');
           this.hiddenVariantInput = option_id.querySelector('input[name="id"]');
           this.dataVaraint = option_id.getAttribute('data-varaints');
            // Update variant ID and check available options on page load
          // Update variant ID when any selector changes
          this.getAvailableVariants();
          this.updateVariantId();
          let __this = this;
        // console.log("this.updateVariantIdthis.updateVariantId=>",this.updateVariantId);
          this.variantSelectors.forEach(function(selector) {
            selector.addEventListener('change', () => __this.updateVariantId());
          });
      }
      productInfoEvent(thiss){
        const quick_add_more = document.querySelectorAll(".quick-add-more");
        const fieldsetall = document.querySelectorAll('.card-information product-info fieldset.product-form__input--pill');
        fieldsetall.forEach((fieldset) => {
          fieldset.style.display = 'none';
        });
        quick_add_more.forEach((item) => {
          item.addEventListener("click",function(e){
            const parent = e.currentTarget.parentNode;
            e.currentTarget.closest('.grid__item').classList.add("open");
            const fieldsets = parent.querySelectorAll(".product-form__input--pill");
            fieldsets.forEach((fieldset) => {
              fieldset.style.display = 'block';
            });
            parent.querySelector(".product-form").style.display ="block";
          });
        });

          window.addEventListener('click',function(event){
        // var proinfo = document.getElementById('ProductInfo-{{ section.id }}-{{ card_product.id }}');
          var proinfo = thiss;
          if(proinfo === event.target || proinfo.contains(event.target)) return;
          const fieldsets = proinfo.querySelectorAll(".product-form__input--pill");
          fieldsets.forEach((fieldset) => {
            fieldset.style.display = 'none';
          });
          const all_li_open = proinfo.closest('.grid__item');
          if(all_li_open.classList.contains("open")){
            all_li_open.classList.remove("open")
          }
          proinfo.querySelector(".product-form").style.display ="none";  
        });  

      }
    getAvailableVariants() {
      const selectedOptions = {};
        var optionName,selectedValue;
      // Gather selected options from the dropdowns
        this.variantSelectors.forEach(function(selector) {
        if(selector.checked == true){
        var optionName = selector.getAttribute('name');
        optionName = optionName.split("-")[0];
        const selectedValue = selector.value;
        if (selectedValue) {
          selectedOptions[optionName] = selectedValue;
        }
        }          
      });

      // Find all available variants that match the selected options
      var obj = JSON.parse(this.dataVaraint);
      const availableVariants = [...obj].filter(function(variant) {
        return Object.keys(selectedOptions).every(function(optionName) {
          const selectedOption = selectedOptions[optionName];
          const optionIndex = variant.options.indexOf(selectedOption);
          return optionIndex !== -1;
        });
      });
      return availableVariants;
    }
    updateVariantId() {
      const availableVariants = this.getAvailableVariants();
      console.log("availableVariantsavailableVariants=>",availableVariants);
      if (availableVariants.length > 0) {
        // Get the first available variant
        this.hiddenVariantInput.value = availableVariants[0].id;
      } else {
        this.hiddenVariantInput.value = ''; // If no available variants, clear the variant ID
      }

      // Dynamically update available options in other select menus
        this.variantSelectors.forEach(function(selector) {
        var selectedValue,optionName;
      // const optionName = selector.getAttribute('id');
        if(selector.checked == true){
        var optionName = selector.getAttribute('name');
        optionName = optionName.split("-")[0];
        const selectedValue = selector.value;
        }
      // If this selector has a value, we need to filter the available options for other selectors
        if (selectedValue) {
          const availableOptionsForOtherSelectors = new Set();
        // Find available options based on the selected value in this selector
          availableVariants.forEach(function(variant) {
            const otherOption = variant.options.filter(function(option, index) {
              return optionName !== variant.options[index];
            });
          // Add the other options that match
            otherOption.forEach(function(option) {
              availableOptionsForOtherSelectors.add(option);
            });
          });
        }
      });
    }
  }
  );
}
